<template>
  <div class="sales-reports-advanced-filter lov-card mb-3">
    <!-- Hàng 1: ngày + cửa hàng + smart search -->
    <b-row class="align-items-end">
      <b-col
        v-if="showDateRange"
        md="6"
        lg="2"
        class="mb-2"
      >
        <label class="filter-label">Từ ngày</label>
        <date-picker
          v-model="local.startDate"
          :config="dpConfigs.date"
          class="form-control form-control-sm"
        />
      </b-col>
      <b-col
        v-if="showDateRange"
        md="6"
        lg="2"
        class="mb-2"
      >
        <label class="filter-label">Đến ngày</label>
        <date-picker
          v-model="local.endDate"
          :config="dpConfigs.date"
          class="form-control form-control-sm"
        />
      </b-col>

      <b-col
        v-if="showStoreMulti"
        md="6"
        lg="3"
        class="mb-2"
      >
        <label class="filter-label">Cửa hàng</label>
        <BDropdownCustom
          v-model="local.storeIds"
          :options="storeDropdownOptions"
          placeholder="cửa hàng"
          :searchable="true"
          :matchKey="['name', 'shortName']"
          :limit="10"
          :compareById="true"
        />
      </b-col>

      <b-col
        v-if="showSmartSearch"
        md="6"
        lg="5"
        class="mb-2"
      >
        <label class="filter-label">Tìm sản phẩm (smart search)</label>
        <b-input-group size="sm">
          <b-input-group-prepend is-text>
            <i class="fas fa-search text-muted" />
          </b-input-group-prepend>
          <b-form-input
            v-model="local.productSearch"
            :placeholder="searchPlaceholder"
            @keyup.enter="apply"
          />
        </b-input-group>
      </b-col>
    </b-row>

    <!-- Hàng 2: chips lọc nâng cao (loại HĐ / brand / DM cấp 1 / NCC / SPC) -->
    <b-row class="align-items-end mt-1">
      <b-col
        v-if="hasField('billType')"
        md="6"
        lg="2"
        class="mb-2"
      >
        <label class="filter-label">Loại HĐ</label>
        <BDropdownCustom
          v-if="multiSelectAdvanced"
          v-model="local.billTypes"
          :options="billTypeDropdownOptions"
          placeholder="loại HĐ"
          :searchable="false"
          :matchKey="['name']"
          :limit="10"
          :compareById="true"
        />
        <b-form-select
          v-else
          v-model="local.billType"
          class="select-style"
          size="sm"
          :options="billTypeOptions"
        />
      </b-col>

      <b-col
        v-if="hasField('brand')"
        md="6"
        lg="2"
        class="mb-2"
      >
        <label class="filter-label">Thương hiệu</label>
        <BDropdownCustom
          v-if="multiSelectAdvanced"
          v-model="local.brandIds"
          :options="brandDropdownOptions"
          placeholder="thương hiệu"
          :searchable="true"
          :matchKey="['name']"
          :limit="10"
          :compareById="true"
        />
        <b-form-select
          v-else
          v-model="local.brandId"
          class="select-style"
          size="sm"
          :options="brandSelectOptions"
        />
      </b-col>

      <b-col
        v-if="hasField('category')"
        md="6"
        lg="2"
        class="mb-2"
      >
        <label class="filter-label">DM cấp 1</label>
        <BDropdownCustom
          v-if="multiSelectAdvanced"
          v-model="local.categoryIds"
          :options="categoryDropdownOptions"
          placeholder="danh mục"
          :searchable="true"
          :matchKey="['name']"
          :limit="10"
          :compareById="true"
        />
        <b-form-select
          v-else
          v-model="local.categoryId"
          class="select-style"
          size="sm"
          :options="categorySelectOptions"
        />
      </b-col>

      <b-col
        v-if="hasField('provider')"
        md="6"
        lg="3"
        class="mb-2"
      >
        <label class="filter-label">Nhà cung cấp</label>
        <BDropdownCustom
          v-if="multiSelectAdvanced"
          v-model="local.providerIds"
          :options="providerDropdownOptions"
          placeholder="nhà cung cấp"
          :searchable="true"
          :remote="true"
          :loading="isSearchingProvider"
          :matchKey="['name', 'shortName']"
          :limit="10"
          :compareById="true"
          @search="onProviderSearch"
        />
        <Autosuggest
          v-else
          :model="providerSearchText"
          :suggestions="filteredProviders"
          placeholder="Tên / mã nhà cung cấp..."
          :limit="10"
          :loading="isSearchingProvider"
          suggestionName="suggestionName"
          @select="onSelectedProvider"
          @change="onProviderSearchChange"
        >
          <template slot="custom" slot-scope="{ suggestion }">
            <div class="d-flex flex-column">
              <span class="text-primary font-weight-bold">{{ suggestion.item.name }} </span>
              <small class="text-muted" v-if="suggestion.item.code">Mã NCC: {{ suggestion.item.code }}</small>
            </div>
          </template>
        </Autosuggest>
      </b-col>

      <b-col
        v-if="hasField('subProvider')"
        md="6"
        lg="3"
        class="mb-2"
      >
        <label class="filter-label">Nhà cung cấp phụ</label>
        <BDropdownCustom
          v-if="multiSelectAdvanced"
          v-model="local.subProviderIds"
          :options="subProviderDropdownOptions"
          placeholder="NCC phụ"
          :searchable="true"
          :remote="true"
          :loading="isSearchingSubProvider"
          :matchKey="['name', 'shortName']"
          :limit="10"
          :compareById="true"
          @search="onSubProviderSearch"
        />
      </b-col>

      <b-col
        v-if="hasField('spc')"
        md="6"
        lg="3"
        class="mb-2"
      >
        <label class="filter-label">SPC</label>
        <b-form-input
          v-model="local.spc"
          size="sm"
          placeholder="Tên sản phẩm cha..."
          @keyup.enter="apply"
        />
      </b-col>

      <b-col
        v-if="hasField('customer')"
        md="6"
        lg="3"
        class="mb-2"
      >
        <label class="filter-label">Khách hàng</label>
        <Autosuggest
          :model="customerSearchText"
          :suggestions="filteredCustomers"
          placeholder="Tên, SĐT, mã khách hàng..."
          :limit="10"
          :loading="isSearchingCustomer"
          suggestionName="suggestionName"
          @select="onSelectedCustomer"
          @change="onCustomerSearchChange"
        >
          <template slot="custom" slot-scope="{ suggestion }">
            <div class="d-flex flex-column">
              <span class="text-primary font-weight-bold">{{ suggestion.item.fullName }} </span>
              <small class="text-muted" v-if="suggestion.item.phoneNo">SĐT: {{ suggestion.item.phoneNo }}</small>
              <small class="text-muted" v-if="suggestion.item.code">Mã KH: {{ suggestion.item.code }}</small>
            </div>
          </template>
        </Autosuggest>
      </b-col>

      <b-col
        cols="12"
        lg="auto"
        class="mb-2 d-flex"
      >
        <b-button
          variant="primary"
          size="sm"
          class="btn-apply mr-2"
          :disabled="loading"
          @click="apply"
        >
          Áp dụng
        </b-button>
        <b-button
          v-if="showDateRange"
          variant="outline-secondary"
          size="sm"
          class="mr-2"
          :disabled="loading"
          @click="clearDateRange"
        >
          Bỏ lọc ngày
        </b-button>
        <b-button
          variant="link"
          size="sm"
          class="text-muted px-0"
          :disabled="loading"
          @click="reset"
        >
          Đặt lại
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import debounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { searchCustomers } from '@/utils/customerSearch';
import { DP_CONFIG } from '@/utils/date';
import { BILL_TYPE_OPTIONS } from '../constants';
import SalesReportsService from '../services/SalesReportsService';

const emptyMultiFilters = () => ({
  billTypes: [],
  brandIds: [],
  categoryIds: [],
  providerIds: [],
  subProviderIds: [],
});

export default {
  name: 'SalesReportsAdvancedFilter',
  components: { BDropdownCustom, Autosuggest },
  props: {
    filters: { type: Object, required: true },
    stores: { type: Array, default: () => [] },
    filterFields: { type: Array, default: () => [] },
    advancedFields: { type: Array, default: () => [] },
    multiSelectAdvanced: { type: Boolean, default: false },
    options: {
      type: Object,
      default: () => ({ brands: [], categories: [], providers: [] }),
    },
    searchPlaceholder: { type: String, default: 'iphone 15 pro...' },
    hideDateFilter: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      dpConfigs: DP_CONFIG,
      local: this.buildLocalState(this.filters),
      billTypeOptions: BILL_TYPE_OPTIONS,
      customerSearchText: '',
      filteredCustomers: [],
      isSearchingCustomer: false,
      providerRemoteOptions: [],
      isSearchingProvider: false,
      providerSearchText: '',
      filteredProviders: [],
      subProviderRemoteOptions: [],
      isSearchingSubProvider: false,
    };
  },
  computed: {
    showDateRange() {
      return !this.hideDateFilter && this.filterFields.includes('date');
    },
    showStoreMulti() {
      return this.filterFields.includes('store');
    },
    showSmartSearch() {
      return this.filterFields.includes('smartSearch');
    },
    storeDropdownOptions() {
      return this.stores.map((store) => ({
        id: store.id,
        name: store.name || store.shortName,
        shortName: store.shortName,
      }));
    },
    billTypeDropdownOptions() {
      return BILL_TYPE_OPTIONS.filter((option) => option.value != null).map((option) => ({
        id: option.value,
        name: option.text,
        shortName: String(option.value),
      }));
    },
    brandDropdownOptions() {
      return (this.options.brands || []).map((brand) => ({
        id: brand.id,
        name: brand.name,
        shortName: brand.name,
      }));
    },
    categoryDropdownOptions() {
      return (this.options.categories || []).map((category) => ({
        id: category.id,
        name: category.name,
        shortName: category.name,
      }));
    },
    providerDropdownOptions() {
      return this.providerRemoteOptions.map((provider) => ({
        id: provider.id,
        name: provider.code ? `${provider.name} (${provider.code})` : provider.name,
        shortName: provider.code || provider.name,
      }));
    },
    subProviderDropdownOptions() {
      return this.subProviderRemoteOptions.map((provider) => ({
        id: provider.id,
        name: provider.code ? `${provider.name} (${provider.code})` : provider.name,
        shortName: provider.code || provider.name,
      }));
    },
    brandSelectOptions() {
      return [
        { value: null, text: 'Tất cả thương hiệu' },
        ...(this.options.brands || []).map((b) => ({ value: b.id, text: b.name })),
      ];
    },
    categorySelectOptions() {
      return [
        { value: null, text: 'Tất cả DM cấp 1' },
        ...(this.options.categories || []).map((c) => ({ value: c.id, text: c.name })),
      ];
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(value) {
        this.local = this.buildLocalState(value);
        if (!value.customerId) {
          this.customerSearchText = '';
          this.filteredCustomers = [];
        }
        if (!value.providerId) {
          this.providerSearchText = '';
          this.filteredProviders = [];
        }
      },
    },
    'local.providerIds': {
      deep: true,
      handler(nextIds, prevIds) {
        if (JSON.stringify(nextIds) === JSON.stringify(prevIds)) {
          return;
        }
        this.local.subProviderIds = [];
        if (this.normalizeProviderIds(nextIds).length) {
          this.loadSubProviderOptions('');
        } else {
          this.subProviderRemoteOptions = [];
        }
      },
    },
  },
  methods: {
    buildLocalState(value = {}) {
      return {
        ...value,
        storeIds: Array.isArray(value.storeIds) ? value.storeIds : [],
        billTypes: Array.isArray(value.billTypes) ? value.billTypes : [],
        brandIds: Array.isArray(value.brandIds) ? value.brandIds : [],
        categoryIds: Array.isArray(value.categoryIds) ? value.categoryIds : [],
        providerIds: Array.isArray(value.providerIds) ? value.providerIds : [],
        subProviderIds: Array.isArray(value.subProviderIds) ? value.subProviderIds : [],
      };
    },
    hasField(name) {
      return this.advancedFields.includes(name);
    },
    normalizeProviderIds(ids = this.local.providerIds) {
      return (Array.isArray(ids) ? ids : [])
        .map((item) => (typeof item === 'object' && item !== null ? item.id : item))
        .filter((id) => id !== undefined && id !== null && id !== '');
    },
    async loadSubProviderOptions(keyword = '') {
      const trimmed = (keyword || '').trim();
      const hasMainProviders = this.normalizeProviderIds().length > 0;
      // Chưa chọn NCC chính: chỉ gọi server khi user bắt đầu gõ
      if (!hasMainProviders && !trimmed) {
        this.subProviderRemoteOptions = [];
        return;
      }
      this.isSearchingSubProvider = true;
      try {
        this.subProviderRemoteOptions = await SalesReportsService.searchSubProviders(
          trimmed,
          20,
          this.local.providerIds,
        );
      } catch (error) {
        this.subProviderRemoteOptions = [];
      } finally {
        this.isSearchingSubProvider = false;
      }
    },
    apply() {
      const payload = { ...this.local };
      if (!this.multiSelectAdvanced) {
        Object.assign(payload, emptyMultiFilters());
      }
      this.$emit('apply', payload);
    },
    clearDateRange() {
      this.local.startDate = '';
      this.local.endDate = '';
      this.apply();
    },
    reset() {
      this.$emit('reset');
    },
    onSelectedCustomer(option) {
      if (!option || !option.item) {
        this.local.customerId = null;
        this.customerSearchText = '';
        return;
      }
      const customer = option.item;
      this.local.customerId = customer.id;
      this.customerSearchText = customer.fullName ? `${customer.fullName} (${customer.phoneNo || customer.code || ''})` : customer.phoneNo;
    },
    onCustomerSearchChange(text) {
      this.customerSearchText = text;
      if (!text || !text.trim()) {
        this.local.customerId = null;
        this.filteredCustomers = [];
        return;
      }
      this.debounceSearchCustomer(text);
    },
    debounceSearchCustomer: debounce(async function (text) {
      if (!text || !text.trim()) return;
      this.isSearchingCustomer = true;
      try {
        const items = await searchCustomers(text);
        this.filteredCustomers = items.map((c) => ({
          id: c.id,
          fullName: c.fullName || '',
          phoneNo: c.phoneNo || c.customerPhone || '',
          code: c.code || c.customerCode || '',
          suggestionName: c.fullName ? `${c.fullName} (${c.phoneNo || c.code || ''})` : c.phoneNo || '',
        }));
      } catch (error) {
        this.filteredCustomers = [];
      } finally {
        this.isSearchingCustomer = false;
      }
    }, TIME_TRIGGER),
    // NCC multi-select (BDropdownCustom remote): BDropdownCustom đã tự debounce trước khi emit
    async onProviderSearch(keyword) {
      this.isSearchingProvider = true;
      try {
        this.providerRemoteOptions = await SalesReportsService.searchProviders(keyword);
      } catch (error) {
        this.providerRemoteOptions = [];
      } finally {
        this.isSearchingProvider = false;
      }
    },
    async onSubProviderSearch(keyword) {
      await this.loadSubProviderOptions(keyword);
    },
    // NCC single-select (Autosuggest) cho tab Nhập hàng
    onSelectedProvider(option) {
      if (!option || !option.item) {
        this.local.providerId = null;
        this.providerSearchText = '';
        return;
      }
      const provider = option.item;
      this.local.providerId = provider.id;
      this.providerSearchText = provider.code
        ? `${provider.name} (${provider.code})`
        : provider.name;
    },
    onProviderSearchChange(text) {
      this.providerSearchText = text;
      if (!text || !text.trim()) {
        this.local.providerId = null;
        this.filteredProviders = [];
        return;
      }
      this.debounceSearchProvider(text);
    },
    debounceSearchProvider: debounce(async function (text) {
      if (!text || !text.trim()) return;
      this.isSearchingProvider = true;
      try {
        const items = await SalesReportsService.searchProviders(text);
        this.filteredProviders = items.map((p) => ({
          id: p.id,
          name: p.name || '',
          code: p.code || '',
          suggestionName: p.code ? `${p.name} (${p.code})` : p.name || '',
        }));
      } catch (error) {
        this.filteredProviders = [];
      } finally {
        this.isSearchingProvider = false;
      }
    }, TIME_TRIGGER),
  },
};
</script>

<style scoped>
.sales-reports-advanced-filter {
  padding: 1rem 1.25rem;
}
.filter-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}
.lov-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}
.btn-apply {
  border-radius: 8px;
  font-weight: 500;
}
</style>
